import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

const CallQueueManager = () => {
  const [queues, setQueues] = useState([]);
  const [waitingCalls, setWaitingCalls] = useState([]);
  const [selectedQueue, setSelectedQueue] = useState('all');

  useEffect(() => {
    // Mock queue data
    const mockQueues = [
      {
        id: 'sales',
        name: 'Satış',
        waitingCalls: 5,
        avgWaitTime: '1:45',
        longestWait: '4:23',
        availableAgents: 3,
        totalAgents: 8,
        priority: 'high'
      },
      {
        id: 'support',
        name: 'Teknik Destek',
        waitingCalls: 12,
        avgWaitTime: '3:12',
        longestWait: '8:45',
        availableAgents: 1,
        totalAgents: 6,
        priority: 'critical'
      },
      {
        id: 'billing',
        name: 'Faturalama',
        waitingCalls: 3,
        avgWaitTime: '0:58',
        longestWait: '2:15',
        availableAgents: 2,
        totalAgents: 4,
        priority: 'normal'
      }
    ];

    const mockWaitingCalls = [
      {
        id: 1,
        customerName: "Mehmet Yılmaz",
        phoneNumber: "+90 532 123 4567",
        queue: 'sales',
        waitTime: 245,
        priority: 'high',
        callbackRequested: false,
        customerType: 'premium'
      },
      {
        id: 2,
        customerName: "Ayşe Demir",
        phoneNumber: "+90 505 987 6543",
        queue: 'support',
        waitTime: 525,
        priority: 'critical',
        callbackRequested: true,
        customerType: 'standard'
      },
      {
        id: 3,
        customerName: "Emre Kaya",
        phoneNumber: "+90 541 456 7890",
        queue: 'billing',
        waitTime: 78,
        priority: 'normal',
        callbackRequested: false,
        customerType: 'premium'
      },
      {
        id: 4,
        customerName: "Zeynep Özkan",
        phoneNumber: "+90 533 234 5678",
        queue: 'sales',
        waitTime: 156,
        priority: 'high',
        callbackRequested: false,
        customerType: 'standard'
      },
      {
        id: 5,
        customerName: "Burak Çelik",
        phoneNumber: "+90 506 345 6789",
        queue: 'support',
        waitTime: 389,
        priority: 'normal',
        callbackRequested: true,
        customerType: 'premium'
      }
    ];

    setQueues(mockQueues);
    setWaitingCalls(mockWaitingCalls);
  }, []);

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical': return 'bg-red-100 text-red-800 border-red-200';
      case 'high': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'normal': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getQueueColor = (queueId) => {
    switch (queueId) {
      case 'sales': return 'bg-blue-100 text-blue-800';
      case 'support': return 'bg-purple-100 text-purple-800';
      case 'billing': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatWaitTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs?.toString()?.padStart(2, '0')}`;
  };

  const handleCallAction = (callId, action) => {
    console.log(`Queue action: ${action} for call ${callId}`);
    // Simulate queue management actions
  };

  const filteredCalls = selectedQueue === 'all' 
    ? waitingCalls 
    : waitingCalls?.filter(call => call?.queue === selectedQueue);

  return (
    <div className="space-y-6">
      {/* Queue Overview */}
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm">
        <div className="p-6 border-b border-slate-200">
          <h3 className="text-lg font-semibold text-slate-900">Kuyruk Yönetimi</h3>
          <p className="text-sm text-slate-600 mt-1">Bekleyen aramalar ve kuyruk durumu</p>
        </div>
        
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            {queues?.map((queue) => (
              <div key={queue?.id} className="border border-slate-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium text-slate-900">{queue?.name}</h4>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${getPriorityColor(queue?.priority)}`}>
                    {queue?.priority === 'critical' ? 'Kritik' :
                     queue?.priority === 'high' ? 'Yüksek' : 'Normal'}
                  </span>
                </div>
                
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Bekleyen:</span>
                    <span className="font-medium">{queue?.waitingCalls}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Ort. Bekleme:</span>
                    <span className="font-medium">{queue?.avgWaitTime}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">En Uzun:</span>
                    <span className="font-medium text-red-600">{queue?.longestWait}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Müsait Temsilci:</span>
                    <span className="font-medium">{queue?.availableAgents}/{queue?.totalAgents}</span>
                  </div>
                </div>
                
                <div className="mt-3 bg-slate-100 rounded-full h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${(queue?.availableAgents / queue?.totalAgents) * 100}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* Waiting Calls List */}
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm">
        <div className="p-6 border-b border-slate-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-slate-900">Bekleyen Aramalar</h3>
            <div className="flex items-center space-x-2">
              <select
                value={selectedQueue}
                onChange={(e) => setSelectedQueue(e?.target?.value)}
                className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">Tüm Kuyruklar</option>
                <option value="sales">Satış</option>
                <option value="support">Teknik Destek</option>
                <option value="billing">Faturalama</option>
              </select>
              <button className="p-2 text-slate-600 hover:text-slate-900 border border-slate-200 rounded-lg hover:bg-slate-50">
                <Icon name="RefreshCw" size={16} />
              </button>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Müşteri
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Kuyruk
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Bekleme Süresi
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Öncelik
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Durum
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">
                  İşlemler
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-200">
              {filteredCalls?.map((call) => (
                <tr key={call?.id} className="hover:bg-slate-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-slate-900">{call?.customerName}</div>
                      <div className="text-sm text-slate-500">{call?.phoneNumber}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getQueueColor(call?.queue)}`}>
                      {call?.queue === 'sales' ? 'Satış' :
                       call?.queue === 'support' ? 'Teknik Destek' : 'Faturalama'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-mono font-medium text-slate-900">
                      {formatWaitTime(call?.waitTime)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full border ${getPriorityColor(call?.priority)}`}>
                      {call?.priority === 'critical' ? 'Kritik' :
                       call?.priority === 'high' ? 'Yüksek' : 'Normal'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      {call?.customerType === 'premium' && (
                        <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full">
                          <Icon name="Crown" size={10} className="mr-1" />
                          Premium
                        </span>
                      )}
                      {call?.callbackRequested && (
                        <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                          <Icon name="PhoneCall" size={10} className="mr-1" />
                          Geri Arama
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => handleCallAction(call?.id, 'priority')}
                        className="text-orange-600 hover:text-orange-900"
                        title="Öncelik Ver"
                      >
                        <Icon name="ArrowUp" size={16} />
                      </button>
                      <button
                        onClick={() => handleCallAction(call?.id, 'transfer')}
                        className="text-blue-600 hover:text-blue-900"
                        title="Kuyruk Değiştir"
                      >
                        <Icon name="ArrowRight" size={16} />
                      </button>
                      <button
                        onClick={() => handleCallAction(call?.id, 'callback')}
                        className="text-green-600 hover:text-green-900"
                        title="Geri Arama Planla"
                      >
                        <Icon name="Calendar" size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default CallQueueManager;